import Dexie, { type EntityTable } from "dexie";

// ── Table types ─────────────────────────────────────────────────────

/** FLHA draft stored locally for offline access */
export interface FlhaDraft {
  /** Local draft ID (nanoid). Maps to server flhaId when synced. */
  id: string;
  /** Server-side FLHA ID (null if created offline, not yet synced) */
  serverId: string | null;
  /** Full wizard state snapshot (JSON-serializable subset of FlhaWizardState) */
  data: {
    projectId: string | null;
    projectName: string | null;
    crewIds: string[];
    supervisorUserId: string | null;
    workArea: string;
    shift: "day" | "night";
    participants: Array<{
      userId: string;
      name: string;
      email: string;
      included: boolean;
      participated: boolean;
      isNewWorker: boolean;
      signatureFileId?: string | null;
      signedAt?: string | null;
    }>;
    selectedTaskIds: string[];
    hasHighRiskTasks: boolean;
    hazards: Array<{
      hazardCatalogId: string;
      code: string;
      label: string;
      category: string;
      taskCatalogId: string;
      selected: boolean;
      riskBefore: "low" | "moderate" | "high" | null;
      riskAfter: "low" | "moderate" | "high" | null;
      selectedControlIds: string[];
    }>;
    ppeItems: Array<{
      ppeCatalogId: string;
      code: string;
      label: string;
      confirmed: boolean;
    }>;
    ppeInspected: boolean;
    weather: {
      temperature: string;
      wind: string;
      precipitation: string;
      visibility: string;
    };
    currentStep: number;
    totalSteps: number;
    workerNotes?: string;
  };
  /** FLHA status */
  status: "draft" | "submitted" | "approved" | "rejected";
  /** ISO timestamp of last local modification */
  updatedAt: string;
  /** ISO timestamp when last successfully synced to server */
  syncedAt: string | null;
  /** Creator user ID */
  creatorUserId: string;
}

/** Cached catalog item (tasks, hazards, controls, PPE) */
export interface CatalogCache {
  /** Catalog type key (e.g., "tasks", "hazards", "controls", "ppe", "task-hazards", etc.) */
  key: string;
  /** The cached API response data (JSON-serializable) */
  data: unknown;
  /** ISO timestamp of when this cache entry was written */
  cachedAt: string;
  /** ISO timestamp of when the data expires (for staleness check) */
  expiresAt: string;
}

/** Queued mutation for sync when connectivity returns */
export interface SyncQueueItem {
  /** Auto-increment ID for ordering */
  id?: number;
  /** HTTP method */
  method: "POST" | "PUT" | "PATCH" | "DELETE";
  /** API URL path (e.g., "/api/flha") */
  url: string;
  /** Request body (JSON-serializable) */
  body: unknown | null;
  /** ISO timestamp of when the mutation was queued */
  createdAt: string;
  /** Number of sync attempts so far */
  attempts: number;
  /** Last error message if sync failed */
  lastError: string | null;
  /** Status of this queue item */
  status: "pending" | "in-flight" | "failed" | "completed";
  /** Optional reference to the local draft ID this mutation relates to */
  draftId: string | null;
  /** Sync priority (lower = higher priority). 1=FLHA submit, 2=FLHA draft, 3=signatures, 4=photos, 5=assets/permits, 9=other */
  priority: number;
}

/** Sync priority constants -- lower values sync first */
export const SYNC_PRIORITY = {
  FLHA_SUBMIT: 1,
  FLHA_DRAFT: 2,
  SIGNATURE: 3,
  PHOTO: 4,
  ASSET_PERMIT: 5,
  OTHER: 9,
} as const;

// ── v2.0 table types ────────────────────────────────────────────────

/** Cached asset data for offline reference */
export interface AssetCacheItem {
  id: string;
  name: string;
  assetType: string;
  serialNumber: string | null;
  tagNumber: string | null;
  status: string;
  location: string | null;
  cachedAt: string;
}

/** Permit draft stored locally */
export interface PermitDraft {
  id: string;
  serverId: string | null;
  permitType: string;
  projectId: string;
  data: Record<string, unknown>;
  status: string;
  updatedAt: string;
  syncedAt: string | null;
  creatorUserId: string;
}

/** Offline FLHA store for complete offline FLHA creation (Phase 12) */
export interface OfflineFlha {
  id: string;
  serverId: string | null;
  data: Record<string, unknown>;
  status: "draft" | "queued" | "synced" | "failed";
  updatedAt: string;
  syncedAt: string | null;
  creatorUserId: string;
}

/** Toolbox meeting draft stored locally for offline creation */
export interface ToolboxDraft {
  id: string;
  serverId: string | null;
  projectId: string;
  topic: string;
  data: Record<string, unknown>;
  status: "draft" | "queued" | "synced" | "failed";
  updatedAt: string;
  syncedAt: string | null;
  creatorUserId: string;
}

/** Incident report draft stored locally for offline creation */
export interface IncidentDraft {
  id: string;
  serverId: string | null;
  projectId: string;
  incidentType: string;
  data: Record<string, unknown>;
  status: "draft" | "queued" | "synced" | "failed";
  updatedAt: string;
  syncedAt: string | null;
  creatorUserId: string;
}

/** Inspection draft stored locally for offline creation */
export interface InspectionDraft {
  id: string;
  serverId: string | null;
  projectId: string;
  templateId: string | null;
  data: Record<string, unknown>;
  status: "draft" | "queued" | "synced" | "failed";
  updatedAt: string;
  syncedAt: string | null;
  creatorUserId: string;
}

/** Cached certification data for offline reference */
export interface CertCacheItem {
  id: string;
  userId: string;
  certTypeName: string;
  expiryDate: string | null;
  status: string;
  cachedAt: string;
}

// ── Database definition ─────────────────────────────────────────────

const db = new Dexie("pph-safety-offline") as Dexie & {
  drafts: EntityTable<FlhaDraft, "id">;
  catalogs: EntityTable<CatalogCache, "key">;
  syncQueue: EntityTable<SyncQueueItem, "id">;
  assetCache: EntityTable<AssetCacheItem, "id">;
  permitDrafts: EntityTable<PermitDraft, "id">;
  offlineFlha: EntityTable<OfflineFlha, "id">;
  toolboxDrafts: EntityTable<ToolboxDraft, "id">;
  incidentDrafts: EntityTable<IncidentDraft, "id">;
  inspectionDrafts: EntityTable<InspectionDraft, "id">;
  certCache: EntityTable<CertCacheItem, "id">;
};

db.version(1).stores({
  // drafts: primary key "id", indexed on serverId, status, creatorUserId, updatedAt
  drafts: "id, serverId, status, creatorUserId, updatedAt",
  // catalogs: primary key "key" (unique per catalog type)
  catalogs: "key",
  // syncQueue: auto-increment primary key, indexed on status and createdAt for ordered processing
  syncQueue: "++id, status, createdAt",
});

db.version(2).stores({
  // Existing stores (must repeat for Dexie to keep them)
  drafts: "id, serverId, status, creatorUserId, updatedAt",
  catalogs: "key",
  syncQueue: "++id, status, createdAt",
  // New v2 stores -- ALL features in one bump per CONTEXT.md decision
  assetCache: "id, assetType, status",
  permitDrafts: "id, serverId, permitType, projectId, status, updatedAt",
  offlineFlha: "id, serverId, status, creatorUserId, updatedAt",
  toolboxDrafts: "id, serverId, projectId, status, updatedAt",
  incidentDrafts: "id, serverId, projectId, incidentType, status, updatedAt",
  inspectionDrafts: "id, serverId, projectId, status, updatedAt",
  certCache: "id, userId, status",
});

// v3: Add priority index to syncQueue for priority-based sync ordering (Phase 12)
db.version(3)
  .stores({
    drafts: "id, serverId, status, creatorUserId, updatedAt",
    catalogs: "key",
    syncQueue: "++id, status, createdAt, priority",
    assetCache: "id, assetType, status",
    permitDrafts: "id, serverId, permitType, projectId, status, updatedAt",
    offlineFlha: "id, serverId, status, creatorUserId, updatedAt",
    toolboxDrafts: "id, serverId, projectId, status, updatedAt",
    incidentDrafts: "id, serverId, projectId, incidentType, status, updatedAt",
    inspectionDrafts: "id, serverId, projectId, status, updatedAt",
    certCache: "id, userId, status",
  })
  .upgrade((tx) => {
    // Set default priority for existing queue items
    return tx
      .table("syncQueue")
      .toCollection()
      .modify((item: Record<string, unknown>) => {
        item.priority = 9;
      });
  });

export { db };
